import React, { useEffect, useRef, useState } from 'react'
import { Select, TextField, Button, Row, Checkbox, H5 } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { MenuClose } from '@fs/zion-icon/dist/cjs/icons'
import { getFunctionList, getQueriesList, submitQuery } from '../../../services/rftService'
import useNotifications from '../../../hooks/useNotifications'
import getUserSummary from '../../../services/userService'
import { getPerson } from '../../../services/personService'
import { getTree } from '../../../services/treeService'

export default function RftSubmitQuery({ state, dispatch }) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const selectedFunc = state.functions?.find((func) => func.query === state.selectedOption)
  const PERSON_ID = 'person-id'
  const CONTRIBUTOR_ID = 'contrib-id'
  const TREE_ID = 'tree-id'

  // Local help text overrides per param (e.g., resolved name from ID)
  const [paramHelp, setParamHelp] = useState({})
  const latestValueRef = useRef({})

  // Simple validators to avoid firing calls on every keystroke
  const PERSON_USER_REGEX = /^[A-Z0-9]{4}-?[A-Z0-9]{3}$/i
  const TREE_REGEX = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/i
  const IS_NUMBER = /^\d+$/
  const NOT_FOUND = 'Not found'

  const isValidForLookup = (paramName, value) => {
    if (!value || value.trim() === '') return false
    const v = value.trim()
    switch (paramName) {
      case PERSON_ID:
      case CONTRIBUTOR_ID:
        return PERSON_USER_REGEX.test(v) || (IS_NUMBER.test(v) && v.length >= 6)
      case TREE_ID:
        return TREE_REGEX.test(v)
      default:
        return false
    }
  }

  const performLookup = async (paramName, value) => {
    if (!value) {
      setParamHelp((prev) => ({ ...prev, [paramName]: undefined }))
      latestValueRef.current[paramName] = ''
      return
    }

    latestValueRef.current[paramName] = value
    try {
      if (!isValidForLookup(paramName, value)) {
        setParamHelp((prev) => ({ ...prev, [paramName]: undefined }))
        return
      }

      let name = ''
      try {
        if (paramName === CONTRIBUTOR_ID) {
          const response = await getUserSummary(value)
          name = response?.data?.dmcUser?.contactName || response?.data?.dmcUser?.username || ''
        } else if (paramName === PERSON_ID) {
          const response = await getPerson(value, null)
          name = response?.name || ''
        } else if (paramName === TREE_ID) {
          const response = await getTree(value)
          name = response?.data?.name || ''
        }
      } catch (err) {
        if (err.status > 404) {
          error(
            t('rft.queries.getIdError', 'Error getting {idType}: {error}', { idType: paramName, error: err.message }),
            err
          )
        }
      }

      // Guard against out-of-order responses
      if (latestValueRef.current[paramName] !== value) return

      if (name && typeof name === 'string') {
        setParamHelp((prev) => ({ ...prev, [paramName]: name }))
      } else {
        setParamHelp((prev) => ({ ...prev, [paramName]: NOT_FOUND }))
      }
    } catch {
      if (latestValueRef.current[paramName] === value) {
        setParamHelp((prev) => ({ ...prev, [paramName]: NOT_FOUND }))
      }
    }
  }

  const getHelpMessage = (paramName) => {
    if (paramHelp[paramName] && paramHelp[paramName] !== NOT_FOUND) {
      // valid name found, return nothing
      return null
    }
    if (paramHelp[paramName] === NOT_FOUND) {
      return t('common.notFound', 'Not found')
    }
    return t(`rft.queries.params.${paramName}`, paramName)
  }

  const getParamTextField = (paramName, required) => (
    <TextField
      label={
        required
          ? `${t('rft.queries.parameter', 'Parameter')}:`
          : `${t('rft.queries.optionalParameter', 'Parameter (Optional)')}:`
      }
      helpMessage={getHelpMessage(paramName)}
      validMessage={(!getHelpMessage(paramName) && paramHelp[paramName]) || ''}
      type="text"
      value={state.params[paramName]}
      onChange={(e) => {
        dispatch({ type: 'SET_PARAMS', value: e.target.value, paramName })
        performLookup(paramName, e.target.value)
      }}
    />
  )

  const getParamCheckbox = (paramName) => (
    <Checkbox
      alignY="nextToInput"
      label={t(`rft.queries.params.${paramName}`, paramName)}
      name={paramName}
      checked={state.params[paramName]}
      onChange={(e) => dispatch({ type: 'SET_PARAMS', value: e.target.checked, paramName })}
    />
  )

  const getParamInput = (paramName, required) => {
    const checkboxParams = ['current-only']

    if (checkboxParams.includes(paramName)) {
      return getParamCheckbox(paramName)
    }
    return getParamTextField(paramName, required)
  }

  const refreshListAfterSubmit = () => {
    const newResultPage = state.oldestToNewest ? Math.floor((state.totalElements || 1) / state.pageCount) : 0
    getQueriesList(newResultPage + 1, state.pageCount, state.showAll, state.oldestToNewest)
      .then((response) => {
        dispatch({ type: 'REFRESH_AFTER_SUBMIT', value: response.data, page: newResultPage })
      })
      .catch((err) => {
        error(t('rft.queries.errorRetrievingJobs', 'Error retrieving jobs'), err)
      })
  }

  const submitQueryHandler = () => {
    submitQuery(state.selectedOption, state.params)
      .then(() => {
        ok('Query submitted')
        refreshListAfterSubmit()
      })
      .catch((err) => {
        error(t('rftSubmitQuery.submit.error', 'Error submitting rft query'), err)
      })
  }

  useEffect(() => {
    !state.functions &&
      getFunctionList()
        .then((result) => {
          const functionsList = result.data.map((value) => {
            return { label: t(`rft.queries.${value.query}`, value.query), value: value.query }
          })
          dispatch({ type: 'SET_FUNCTIONS', functions: result.data, functionNames: functionsList })
        })
        .catch((err) => {
          error(t('rft.queries.ErrorRetrievingQueries', 'Error retrieving queries to select'), err)
        })
  }, [dispatch, error, state.functions, t])

  return (
    <>
      <H5>{t('rft.queries.header', 'Submit Data Admin Query')}</H5>
      <Row>
        <Select
          label={`${t('rft.queries.functionNames.function', 'Function')}:`}
          placeholder={t('rft.queries.functionNames.selectQuery', 'Select Query')}
          value={state.selectedOption}
          options={state.functionNames}
          onChange={(e) => dispatch({ type: 'SET_FUNCTION_OPTION', value: e.target.value })}
        />

        {selectedFunc?.parameters?.required?.map((paramName) => {
          return <div key={paramName}>{getParamInput(paramName, true)}</div>
        })}

        {selectedFunc?.parameters?.optional?.map((paramName) => {
          return <div key={paramName}>{getParamInput(paramName, false)}</div>
        })}
      </Row>
      <Row>
        {state.selectedOption && (
          <Button alignY="nextToInput" emphasis="high" onClick={submitQueryHandler}>
            {t('rft.queries.submit', 'Submit')}
          </Button>
        )}
        <Button
          alignY="nextToInput"
          emphasis="medium"
          Icon={MenuClose}
          onClick={() => dispatch({ type: 'TOGGLE_SHOW_SUBMIT' })}
        >
          {t('rft.queries.cancel', 'Cancel')}
        </Button>
      </Row>
    </>
  )
}
